import UnitV2 from 'app/shared/models/UnitV2';
import Photo from 'app/shared/models/Photo';
import ModelCollection from 'app/shared/models/ModelCollection';
import assign from 'lodash/assign';
import isFinite from 'lodash/isFinite';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import type { SummaryListingDetails } from 'app/types/summaryListingDetails.type';
import type Pricing from 'app/shared/models/Pricing';
import ModelSummary from 'app/shared/models/ModelSummary';

interface ModelSummaryData {
  bedsLow?: number;
  bedsHigh?: number;
  sqFtLow?: number;
  sqFtHigh?: number;
  bathsLow?: number;
  bathsHigh?: number;
  priceLow?: number;
  priceHigh?: number;
}

interface PricingSummaryResult {
  Studio: Partial<ModelSummaryData>;
  1: Partial<ModelSummaryData>;
  2: Partial<ModelSummaryData>;
  3: Partial<ModelSummaryData>;
  ThreePlus: Partial<ModelSummaryData>;
  summary: Partial<ModelSummaryData>;
}

interface FloorplanOrModel {
  numBeds?: number;
  lowPrice?: number;
  highPrice?: number;
  beds?: number;
  baths?: number;
  low?: number;
  high?: number;
  sqft?: number;
  priceLow?: number;
  priceHigh?: number;
  bedsLow?: number;
  bedsHigh?: number;
}

interface BuildingListingDetails extends SummaryListingDetails {
  floorplans?: Array<FloorplanOrModel>;
}

const buildingUtils = {
  createPricingSummary(pricing: Pricing, pricingSummary?: PricingSummaryResult): PricingSummaryResult {
    const defaultSummary: PricingSummaryResult = {
      Studio: {},
      1: {},
      2: {},
      3: {},
      ThreePlus: {},
      summary: {},
    };
    const newSummary: PricingSummaryResult = assign({}, pricingSummary ?? defaultSummary) as PricingSummaryResult;
    if (pricing.Studio) {
      if (!newSummary.Studio.priceLow || newSummary.Studio.priceLow > pricing.Studio.priceLow) {
        newSummary.Studio.priceLow = pricing.Studio.priceLow;
      }
      if (!newSummary.Studio.priceHigh || newSummary.Studio.priceHigh < pricing.Studio.priceHigh) {
        newSummary.Studio.priceHigh = pricing.Studio.priceHigh;
      }
      const bedsLow = newSummary.summary.bedsLow;
      if (!isFinite(bedsLow ?? NaN) || (bedsLow !== undefined && bedsLow > 0)) {
        newSummary.summary.bedsLow = 0;
      }
      const bedsHigh = newSummary.summary.bedsHigh;
      if (!isFinite(bedsHigh ?? NaN) || (bedsHigh !== undefined && bedsHigh < 0)) {
        newSummary.summary.bedsHigh = 0;
      }
    }
    if (pricing[1]) {
      if (!newSummary[1].priceLow || newSummary[1].priceLow > pricing[1].priceLow) {
        newSummary[1].priceLow = pricing[1].priceLow;
      }
      if (!newSummary[1].priceHigh || newSummary[1].priceHigh < pricing[1].priceHigh) {
        newSummary[1].priceHigh = pricing[1].priceHigh;
      }
      const bedsLow = newSummary.summary.bedsLow;
      if (!isFinite(bedsLow ?? NaN) || (bedsLow !== undefined && bedsLow > 1)) {
        newSummary.summary.bedsLow = 1;
      }
      const bedsHigh = newSummary.summary.bedsHigh;
      if (!isFinite(bedsHigh ?? NaN) || (bedsHigh !== undefined && bedsHigh < 1)) {
        newSummary.summary.bedsHigh = 1;
      }
    }
    if (pricing[2]) {
      if (!newSummary[2].priceLow || newSummary[2].priceLow > pricing[2].priceLow) {
        newSummary[2].priceLow = pricing[2].priceLow;
      }
      if (!newSummary[2].priceHigh || newSummary[2].priceHigh < pricing[2].priceHigh) {
        newSummary[2].priceHigh = pricing[2].priceHigh;
      }
      const bedsLow = newSummary.summary.bedsLow;
      if (!isFinite(bedsLow ?? NaN) || (bedsLow !== undefined && bedsLow > 2)) {
        newSummary.summary.bedsLow = 2;
      }
      const bedsHigh = newSummary.summary.bedsHigh;
      if (!isFinite(bedsHigh ?? NaN) || (bedsHigh !== undefined && bedsHigh < 2)) {
        newSummary.summary.bedsHigh = 2;
      }
    }
    if (pricing[3]) {
      if (!newSummary[3].priceLow || newSummary[3].priceLow > pricing[3].priceLow) {
        newSummary[3].priceLow = pricing[3].priceLow;
      }
      if (!newSummary[3].priceHigh || newSummary[3].priceHigh < pricing[3].priceHigh) {
        newSummary[3].priceHigh = pricing[3].priceHigh;
      }
      const bedsLow = newSummary.summary.bedsLow;
      if (!isFinite(bedsLow ?? NaN) || (bedsLow !== undefined && bedsLow > 3)) {
        newSummary.summary.bedsLow = 3;
      }
      const bedsHigh = newSummary.summary.bedsHigh;
      if (!isFinite(bedsHigh ?? NaN) || (bedsHigh !== undefined && bedsHigh < 3)) {
        newSummary.summary.bedsHigh = 3;
      }
    }

    if (pricing.ThreePlus && pricing.ThreePlus instanceof ModelSummary) {
      if (!newSummary.ThreePlus.priceLow || newSummary.ThreePlus.priceLow > pricing.ThreePlus.priceLow) {
        newSummary.ThreePlus.priceLow = pricing.ThreePlus.priceLow;
      }
      if (!newSummary.ThreePlus.priceHigh || newSummary.ThreePlus.priceHigh < pricing.ThreePlus.priceHigh) {
        newSummary.ThreePlus.priceHigh = pricing.ThreePlus.priceHigh;
      }
      const bedsLow = newSummary.summary.bedsLow;
      if (!isFinite(bedsLow ?? NaN) || (bedsLow !== undefined && bedsLow > pricing.ThreePlus.bedsLow)) {
        newSummary.summary.bedsLow = pricing.ThreePlus.bedsLow;
      }
      const bedsHigh = newSummary.summary.bedsHigh;
      if (!isFinite(bedsHigh ?? NaN) || (bedsHigh !== undefined && bedsHigh < pricing.ThreePlus.bedsHigh)) {
        newSummary.summary.bedsHigh = pricing.ThreePlus.bedsHigh;
      }
    }

    return newSummary;
  },
  processApiDataForBuildingV2({
    areas = {},
    availableUnits = {},
    geo = {},
    listings = [],
    lotIdReviews = {},
    schools = [],
  }: {
    areas?: Record<string, unknown>;
    availableUnits?: { rental?: Array<unknown> };
    geo?: Record<string, unknown>;
    listings: Array<BuildingListingDetails>;
    lotIdReviews?: Record<string, unknown>;
    schools?: Array<unknown>;
  }) {
    let buildingActivated = 0;
    let buildingUserItemTypes: Array<string> = [];
    const buildingPhotos: Array<Photo> = [];
    const buildingLargePhotos: Array<Photo> = [];
    const buildingThumbPhotos: Array<Photo> = [];
    const seenPhotoUrls = new Set<string>();
    const seenLargePhotoUrls = new Set<string>();
    const seenThumbPhotoUrls = new Set<string>();
    let isActive = true;
    const pricingSummary: Record<string, PricingSummaryResult> = {};
    const modelCollection = ModelCollection();

    const rentalUnits = availableUnits.rental || [];
    const hasMultipleUnits = rentalUnits.length > 1;

    const units = map(listings, (listing) => {
      // @ts-expect-error - UnitV2 constructor has complex typing issues with Amenities
      const unit = new UnitV2(listing, {
        areas,
        geo,
        belongsToMultipleUnitBuilding: hasMultipleUnits,
        forRentUnitsInBuilding: rentalUnits,
        lotIdReviews,
        schools,
      }) as unknown as
        | {
            activated: number;
            userItemTypes: Array<string>;
            photos: Array<{ url: string }>;
            largePhotos: Array<{ url: string }>;
            thumbPhotos: Array<{ url: string }>;
            unit: string;
            pricing?: Pricing;
            listingType: string;
          }
        | false;

      if (!unit) {
        return unit;
      }

      buildingActivated = unit.activated > buildingActivated ? unit.activated : buildingActivated;
      buildingUserItemTypes = buildingUserItemTypes.concat(unit.userItemTypes);

      if (listing && listing.active === false) {
        isActive = false;
      }

      if (unit.photos[0] && !seenPhotoUrls.has(unit.photos[0].url)) {
        seenPhotoUrls.add(unit.photos[0].url);
        buildingPhotos.push(
          new Photo({
            url: unit.photos[0].url,
            caption: `Photo of Unit ${unit.unit}`,
          }),
        );
      }
      if (unit.largePhotos[0] && !seenLargePhotoUrls.has(unit.largePhotos[0].url)) {
        seenLargePhotoUrls.add(unit.largePhotos[0].url);
        buildingLargePhotos.push(
          new Photo({
            url: unit.largePhotos[0].url,
            caption: `Photo of Unit ${unit.unit}`,
          }),
        );
      }
      if (unit.thumbPhotos[0] && !seenThumbPhotoUrls.has(unit.thumbPhotos[0].url)) {
        seenThumbPhotoUrls.add(unit.thumbPhotos[0].url);
        buildingThumbPhotos.push(
          new Photo({
            url: unit.thumbPhotos[0].url,
            caption: `Photo of Unit ${unit.unit}`,
          }),
        );
      }

      /**
       * UnitV2 uses `floorplans` param
       * SummaryV2 uses `models` param
       */
      modelCollection.add(listing.floorplans, listing.listingType, listing.propertyType);

      if (unit.pricing) {
        const listingType = unit.listingType === 'corporate' ? 'rental' : unit.listingType;
        pricingSummary[listingType] = buildingUtils.createPricingSummary(unit.pricing, pricingSummary[listingType]);
      }

      return unit;
    });

    return {
      units,
      rentalUnits,
      pricingSummary,
      isActive,
      buildingPhotos,
      buildingLargePhotos,
      buildingThumbPhotos,
      buildingActivated,
      buildingUserItemTypes: uniq(buildingUserItemTypes),
      listingMinMaxPriceBeds: modelCollection.summarize(),
    };
  },
  processApiDataForSummaryV2(
    listings: Array<
      SummaryListingDetails & {
        modelSummary?: { baths?: number };
        activated?: number;
        updated?: number;
        models?: Array<{ baths?: number; [key: string]: unknown }>;
      }
    > = [],
    filter: Record<string, unknown> = {},
  ) {
    let buildingUserItemTypes: Array<string> = [];
    let buildingActivated = 0;
    let buildingUpdated = 0;
    let isActive = true;
    const modelCollection = ModelCollection();

    listings.forEach((listing) => {
      if (!listing.modelSummary) {
        return false;
      }

      buildingActivated = (listing.activated ?? 0) > buildingActivated ? (listing.activated ?? 0) : buildingActivated;
      buildingUpdated = (listing.updated ?? 0) > buildingUpdated ? (listing.updated ?? 0) : buildingUpdated;
      buildingUserItemTypes = buildingUserItemTypes.concat(listing.userItemTypes);

      if (listing && listing.active === false) {
        isActive = false;
      }

      /*
       * HPWEB-5871: support test of bath counts in listing card
       * */
      if (listing.models && listing.models.length === 1 && listing.modelSummary?.baths !== undefined) {
        listing.models[0].baths = listing.modelSummary.baths;
      }

      /**
       * SummaryV2 uses `models` param
       * UnitV2 uses `floorplans` param
       */
      modelCollection.add(listing.models, listing.listingType, listing.propertyType);
    });

    return {
      isActive,
      buildingActivated,
      buildingUpdated,
      buildingUserItemTypes: uniq(buildingUserItemTypes),
      listingMinMaxPriceBeds: modelCollection.summarize(filter),
    };
  },
  getBuildingDisplayName(units: Array<{ address: { street?: string; hideStreet?: boolean }; displayName?: string }>) {
    let displayName = units[0].address.street;
    units.forEach((unit) => {
      if (unit.address.hideStreet) {
        displayName = unit.displayName;
      }
    });
    return displayName;
  },
  getBuildingPageTitle({ city, state, street, zip }: { city?: string; state?: string; street?: string; zip?: string }) {
    return `${street}, ${city}, ${state} ${zip}`;
  },
};

export default buildingUtils;
